import { Table } from '@mantine/core';
import { memo } from 'react';
import { rowLabel } from '../../domain/rowLabel.js';
import { NameCell, SelectCell } from './matrixCells.js';
import type { MatrixRow as MatrixRowData, RowSpec } from './rowSpecs.js';
import { StatusCells } from './StatusCells.js';
import type { MatrixColumn, OpenTarget } from './types.js';

export type MatrixRowProps<Row extends MatrixRowData> = {
    spec: RowSpec<Row>;
    row: Row;
    columns: MatrixColumn[];
    selected: boolean;
    onToggle: (row: Row) => void;
    onOpen: (target: OpenTarget) => void;
};

/**
 * One `<Table.Tr>` of `MatrixGrid`, memoised: a keystroke in the search box, a toggle on another
 * row, or any other change upstream that leaves this row's own props unchanged now skips this
 * row entirely instead of re-running `rowLabel`, `spec.extraCell` and every status cell's label.
 * `spec` is one of the module-level `STUDIO_ROWS`/`GAME_ROWS` constants, so it never breaks the
 * memoisation on its own.
 */
const MatrixRowBase = <Row extends MatrixRowData>({
    spec,
    row,
    columns,
    selected,
    onToggle,
    onOpen,
}: MatrixRowProps<Row>) => {
    const name = rowLabel(row.title, row.key).label;
    return (
        <Table.Tr>
            <SelectCell ariaLabel={spec.selectLabel(name)} checked={selected} onChange={() => onToggle(row)} />
            <NameCell to={spec.editHref(row)} ariaLabel={`Edit ${name}`} title={row.title} entityKey={row.key} />
            {spec.extraCell(row, name)}
            <StatusCells
                columns={columns}
                cells={row.cells}
                type={spec.type}
                entityKey={row.key}
                name={name}
                onOpen={onOpen}
            />
        </Table.Tr>
    );
};

export const MatrixRow = memo(MatrixRowBase) as typeof MatrixRowBase;
